/* import __COLOCATED_TEMPLATE__ from './posting-create-make-offer.hbs'; */
import { computed } from '@ember/object';
import { task } from 'ember-concurrency';
import { readOnly, equal, alias } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { scheduleOnce } from '@ember/runloop';
import Component from '@ember/component';
import { debounce } from '@ember/runloop';
import {
  POSTING_CREATE_COVER_EXPENSES_TYPE,
  POSTING_CREATE_EXPEDITE_TYPE
} from "../../constants/routes/posting-create";

const CLASS_NAME = 'SH-c-posting-create-make-offer';
const CLASS_NAME_PROGRESSION = `${CLASS_NAME}__progress-question-wrapper`;
const CLASS_NAME_PROGRESSION_OFFER_READY = `${CLASS_NAME_PROGRESSION}--offer-ready`;
const CLASS_NAME_PROGRESSION_PROJECT_COMPLETE = `${CLASS_NAME_PROGRESSION}--project-complete`;

const SCROLL_TO_FIELD_BEHAVIOR = 'smooth';
const SCROLL_TO_FIELD_DURATION_MS = 300;

/**
 * This would be used by the buyer to make an offer to a freelancer in the direct hire flow
 */
export default Component.extend({
  /**
   * @inheritDoc
   */
  tagName: '',

  CLASS_NAME,
  CLASS_NAME_PROGRESSION_OFFER_READY,
  CLASS_NAME_PROGRESSION_PROJECT_COMPLETE,

  /**
   * Register services
   */
  userCommandService: service('commands/user-commands'),
  errorTracker: service('error-tracker'),
  state: service('states/routes/posting-create'),
  stateManager: service('state-managers/routes/posting-create'),
  session: service(),

  currentUser: readOnly('session.currentUser'),

  /*********************************
   * REQUIRED PARAMS
   *********************************/

  /*********************************
   * OPTIONAL PARAMS
   **********************************/

  /*********************************
   * LIFECYCLE FUNCTIONS
   **********************************/

  /*********************************
   * PRIVATE
   **********************************/

  /**
   * @type {Boolean}    True when the form elements should be disabled
   */
  _areFormElementsDisabled: readOnly('state.areFormElementsDisabled'),

  /**
   * @type {String}     Constant values of the cover expenses enum for the template.
   */
  _constantsCoverExpensesNo: POSTING_CREATE_COVER_EXPENSES_TYPE.NO,
  _constantsCoverExpensesYes: POSTING_CREATE_COVER_EXPENSES_TYPE.YES,

  _constantsExpediteNo: POSTING_CREATE_EXPEDITE_TYPE.NO,
  _constantsExpediteYes: POSTING_CREATE_EXPEDITE_TYPE.YES,

  /**
   * @type {Boolean}    True when cover seller fees yes or no enum has been selected.
   */
  _isCoverSellerFeesNoSelected: equal('_updatedCoverSellerFees', false),
  _isCoverSellerFeesYesSelected: equal('_updatedCoverSellerFees', true),

  /**
   * @type {Boolean}    True when cover expenses yes or no enum has been selected.
   */
  _isCoverExpensesNoSelected: equal('_updatedCoverExpenses', POSTING_CREATE_COVER_EXPENSES_TYPE.NO),
  _isCoverExpensesYesSelected: equal('_updatedCoverExpenses', POSTING_CREATE_COVER_EXPENSES_TYPE.YES),

  _isExpediteNoSelected: equal('_updatedExpedite', POSTING_CREATE_EXPEDITE_TYPE.NO),
  _isExpediteYesSelected: equal('_updatedExpedite', POSTING_CREATE_EXPEDITE_TYPE.YES),

   /**
   * @type {Boolean}    True when the offer amount has changed but the fees have not loaded yet because of the debounce
   */
  offerChanged: false,

  /**
   * @type {Boolean}    If the loading spinner should be shown for creator fees (is task if running or offer is changing in debounce)
   */
  isCreatorFeeLoading: computed(
  'loadHiringFeeStructure.isRunning',
  'offerChanged',
  '_isCoverSellerFeesYesSelected',
  function() {
    return this.loadHiringFeeStructure.isRunning || (this.offerChanged && this._isCoverSellerFeesYesSelected);
  }),

  /**
   * @type {Boolean}    Whether or not the user has selected 'no' about ready to make an offer
   */
  _isProjectCompletedNoSelected: computed(
  'state.isWorkCompleteSelected',
  '_updatedDirectIsWorkComplete',
  function() {
    return this.get('state.isWorkCompleteSelected') && !this.get('_updatedDirectIsWorkComplete');
  }),

  /**
   * @type {Boolean}    Whether or not the user has selected 'yes' about ready to make an offer
   */
  _isProjectCompletedYesSelected: computed(
  'state.isWorkCompleteSelected',
  '_updatedDirectIsWorkComplete',
  function() {
    return this.get('state.isWorkCompleteSelected') && this.get('_updatedDirectIsWorkComplete');
  }),

  /**
   * @type {Boolean}    Whether or not the user has selected 'no' about ready to make an offer
   */
  _isReadyToMakeOfferNoSelected: computed(
  'state.isReadyToMakeOfferSelected',
  '_updatedDirectIsReadyToMakeOffer',
  function() {
    return this.state.isReadyToMakeOfferSelected && !this._updatedDirectIsReadyToMakeOffer;
  }),

  /**
   * @type {Boolean}    Whether or not the user has selected 'yes' about ready to make an offer
   */
  _isReadyToMakeOfferYesSelected: computed(
  'state.isReadyToMakeOfferSelected',
  '_updatedDirectIsReadyToMakeOffer',
  function() {
    return this.state.isReadyToMakeOfferSelected && this._updatedDirectIsReadyToMakeOffer;
  }),

  /**
   * @type {Boolean}    True when we are ready to show the make offer element.
   */
  _isShowMakeOffer: computed('state.isReadyToMakeOfferSelected', 'state.updatedDirectIsReadyToMakeOffer', function() {
    return this.get('state.isReadyToMakeOfferSelected') && this.get('state.updatedDirectIsReadyToMakeOffer');
  }),

  /**
   * The user's input parsed as a float.
   *
   * @type {Float}
   */
  _offerValueFloat: computed('_updatedDirectOfferAmount', function() {
    let offerValue = parseFloat(this.get('_updatedDirectOfferAmount'));
    if (!offerValue) {
      return 0;
    }

    return offerValue;
  }),

  /**
   * Formatted string of the freelancer payout amount.
   *
   * @type {String}
   */
  _offerValueFormatted: computed('_offerValueFloat', function() {
    return parseFloat(this.get('_offerValueFloat')).toFixed(2);
  }),

  /**
   * Latest feeStructure value based on the estimated addedLifetimeEarningsInCents from current project price input
   *
   * @type {feeStructure}
   */
  feeStructureBasedOnOffer: computed('loadHiringFeeStructure.last.value', function() {
    return this.loadHiringFeeStructure.last?.value;
  }),

  /**
   * The service fee percentage
   *
   * @type {Float}
   */
  serviceFeePercent: computed('state.updatedDirectFreelancerUser.hiringFeeStructure.buyerBaseFee', function() {
    const buyerBaseFee = this.state.updatedDirectFreelancerUser.hiringFeeStructure?.buyerBaseFee ?? 0;
    return buyerBaseFee * 100
  }),

  /**
   * The service fee calculated and rounded as a float.
   *
   * @type {Float}
   */
  _serviceFeeFloat: computed('_offerValueFloat', 'serviceFeePercent', function() {
    if (!this.serviceFeePercent) {
      return 0;
    }

    return Math.round(this._offerValueFloat * this.serviceFeePercent) / 100;
  }),

  /**
   * The service fee percentage
   *
   * @type {Float}
   */
  creatorFeePercent: computed('feeStructureBasedOnOffer.sellerBaseFee', function() {
    const sellerFee = this.feeStructureBasedOnOffer?.sellerBaseFee ?? 0;
    return sellerFee * 100
  }),

  /**
   * The creator fee calculated and rounded as a float.
   *
   * @type {Float}
   */
  creatorFeeFloat: computed('_offerValueFloat', 'creatorFeePercent', function() {
    if (!this.creatorFeePercent) {
      return 0;
    }

    return Math.round(this._offerValueFloat * this.creatorFeePercent) / 100;
  }),

  /**
   * The total value (service fee + freelancer fee)
   *
   * @type {Float}
   */
  _total: computed('_offerValueFloat', '_serviceFeeFloat', 'creatorFeeFloat', '_isCoverSellerFeesYesSelected', function() {
    let offerValue = this._offerValueFloat;

    if (this._isCoverSellerFeesYesSelected) {
      offerValue += this.creatorFeeFloat;
    }

    return offerValue + this._serviceFeeFloat;
  }),

  /**
   * @type {Boolean}
   */
  _updatedCoverSellerFees: readOnly('state.updatedCoverSellerFees'),

  /**
   * @type {String}   of type POSTING_CREATE_COVER_EXPENSES_TYPE.*
   */
  _updatedCoverExpenses: readOnly('state.updatedCoverExpenses'),

  _updatedExpedite: readOnly('state.updatedExpedite'),

  /**
   * NOTE --- ALIASED TO TOP LEVEL STATE.
   *
   * @type {Float}    Amount of the offer
   */
  _updatedDirectOfferAmount: alias('state.updatedDirectOfferAmount'),

  /**
   * @type {Boolean|null}   True when the user is ready to make offer - null if never selected
   */
  _updatedDirectIsReadyToMakeOffer: readOnly('state.updatedDirectIsReadyToMakeOffer'),

  /**
   * @type {Boolean|null}   True or false if user selected if work is complete - null if never selected.
   */
  _updatedDirectIsWorkComplete: readOnly('state.updatedDirectIsWorkComplete'),

  /**
   * Loads the hiringFeeStructure based on the user inputted offer amount and the current user/company earnings
   */
  loadHiringFeeStructure: task(function *() {
    const addedLifetimeWorkInCents = this._offerValueFloat * 100;

    try {
      const response = yield this.userCommandService.getHiringFeeStructure(
        this.state.updatedDirectFreelancerUser.id,
        this.currentUser.company.id,
        addedLifetimeWorkInCents,
      );
      this.set('offerChanged', false);
      return response;
    } catch (error) {
      this.errorTracker.error('Unable to load hiring fee structure', this, error);
    }
  }).restartable(),

  getHiringFeeStructure() {
    return this.loadHiringFeeStructure.perform();
  },

  actions: {
    onInputTextChanged(value) {
      this.setProperties({
        'state.errors': {
          ...this.state.errors,
          amount: undefined,
        },
        offerChanged: true,
      });

      this.stateManager.updateDirectOfferAmount(value);
      if (this._isCoverSellerFeesYesSelected) {
        debounce(this, this.getHiringFeeStructure, 500);
      }
    },

    /**
     * Called when user clicks to select if the work is complete or not.
     *
     * @param {Boolean} isWorkComplete
     */
    onProjectCompletedClicked: function(isWorkComplete) {
      const isOfferValid = this.get('stateManager').isDirectHireOfferValid();

      if (!isOfferValid) return;

      this.get('stateManager').updateIsWorkComplete(isWorkComplete);

      if (isWorkComplete) {
        // scroll new form fields into view after they're rendered
        scheduleOnce('afterRender', this, function() {
          const isProjectCompleteEl = document.querySelector(`.${CLASS_NAME_PROGRESSION_PROJECT_COMPLETE}`);
          const targetOffset = isProjectCompleteEl.getBoundingClientRect().top + window.scrollY + 40;

          window.scrollTo({
            top: targetOffset,
            behavior: SCROLL_TO_FIELD_BEHAVIOR,
            duration: SCROLL_TO_FIELD_DURATION_MS,
          });
        });
      }
    },

    /**
     * Called when user clicks to make an offer or not.
     *
     * @param {Boolean} isReadyToMakeOffer
     */
    onReadyToMakeOfferClicked(isReadyToMakeOffer) {
      const isOverviewValid = this.stateManager.isDirectHireOverviewValid();

      if (!isOverviewValid) {
        return;
      }

      this.stateManager.updateReadyToMakeOffer(isReadyToMakeOffer);

      if (isReadyToMakeOffer) {
        // scroll new form fields into view after they're rendered
        scheduleOnce('afterRender', this, function() {
          const readyToMakeOfferTitleEl = document.querySelector(`.${CLASS_NAME_PROGRESSION_OFFER_READY}`);
          const targetOffset = readyToMakeOfferTitleEl.getBoundingClientRect().top + window.scrollY + 40;

          window.scrollTo({
            top: targetOffset,
            behavior: SCROLL_TO_FIELD_BEHAVIOR,
            duration: SCROLL_TO_FIELD_DURATION_MS,
          });
        });
      }
    },

    /**
     * Called when user selects a cover creator fees button
     *
     * @param {String} coverSellerFees
     */
    onSelectCoverSellerFees(coverSellerFees) {
      this.set('state.errors', {
        ...this.state.errors,
        seller_fees_covered: undefined,
      });

      this.stateManager.updateCoverSellerFees(coverSellerFees);
      if (this._isCoverSellerFeesYesSelected && this.offerChanged && this._offerValueFloat) {
        this.loadHiringFeeStructure.perform();
      }
    },

    /**
     * Called when user selects a cover expenses button
     *
     * @param {String} coverExpensesType    of type POSTING_CREATE_COVER_EXPENSES_TYPE.*
     */
    onSelectCoverExpenses: function(coverExpensesType) {
      this.set('state.errors', {
        ...this.state.errors,
        expense: undefined,
      });

      this.stateManager.updateCoverExpenses(coverExpensesType);
    },

    onSelectExpedite: function(expediteType) {
      this.set('state.errors', {
        ...this.state.errors,
        expedite: undefined,
      });

      this.stateManager.updateExpedite(expediteType);
    },
  }
});
